<template>
  <div>
    <v-container fluid>
      <v-row justify="center" class="pb-16">
        <v-col cols="12" md="8">
          <v-card class="pa-5">
            <v-card-title class="text-h5">{{ $t('terms.title') }}</v-card-title>
            <v-card-subtitle>
              {{ $t('terms.latestVersionAccepted') }}
              <strong>{{ acceptedVersion }}</strong>
            </v-card-subtitle>
            <v-divider class="mb-3"></v-divider>

            <v-card-text>
              <div v-html="termsContent"></div>
            </v-card-text>

            <v-alert
              v-if="showAgreement"
              type="warning"
              class="mt-3"
              variant="tonal"
            >
              <strong>{{ $t('terms.acceptWarning') }}</strong>
            </v-alert>

            <v-btn
              v-if="showAgreement"
              color="primary"
              class="mt-3"
              @click="acceptTerms"
            >
              {{ $t('terms.acceptButton') }}
            </v-btn>

            <v-alert
              v-if="error"
              type="error"
              variant="tonal"
              closable
              class="mt-3"
            >
              {{ error }}
            </v-alert>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import { mapState } from 'vuex'

  export default {
    name: 'TermsAndConditions',
    data() {
      return {
        acceptedVersion: 0,
        latestVersion: 0,
        termsContent: 'Loading terms...',
        showAgreement: false,
        error: null,
      }
    },
    computed: {
      ...mapState('UserData', ['user']),
      currentLanguage() {
        // Get current language from i18n
        return this.$i18n.locale
      },
    },
    watch: {
      // Watch for language changes and reload terms content
      currentLanguage(newLang, oldLang) {
        if (newLang !== oldLang) {
          console.log(
            `[ViewTerms] Language changed from ${oldLang} to ${newLang}, reloading terms content`
          )
          const versionToLoad =
            this.acceptedVersion > 0 ? this.acceptedVersion : this.latestVersion
          if (versionToLoad > 0) {
            this.loadTermsContent(versionToLoad)
          }
        }
      },
    },
    methods: {
      async fetchLatestVersion() {
        try {
          const configDoc = await db.collection('config').doc('terms').get()
          if (configDoc.exists) {
            this.latestVersion = configDoc.data().latestVersion
          } else {
            throw new Error('Terms configuration is unavailable.')
          }
        } catch (err) {
          console.error('Error fetching latest version:', err)
          this.error = 'Failed to load terms version. Please try again.'
        }
      },

      async loadTermsContent(version) {
        try {
          const termsDoc = await db
            .collection('terms_versions')
            .doc(version.toString())
            .get()
          if (termsDoc.exists) {
            const termsData = termsDoc.data()

            // Get current language from i18n (this will update when language changes)
            const currentLanguage = this.$i18n.locale || 'en'

            // Try to get translated content from variants
            const translatedContent =
              termsData.variants?.default?.[currentLanguage]

            // Fallback to original content if no translation found
            this.termsContent =
              translatedContent ||
              termsData.content ||
              'Terms not found for version ' + version
          } else {
            this.termsContent = 'Terms not found for version ' + version
          }
        } catch (err) {
          console.error('Error loading terms content:', err)
          this.termsContent = 'Failed to load terms content.'
        }
      },

      async checkUserAcceptance() {
        if (!this.user || !this.user.uid) {
          this.error = 'User data is not available. Please log in again.'
          return
        }

        try {
          await this.fetchLatestVersion()

          const querySnapshot = await db
            .collection('users')
            .where('uid', '==', this.user.uid)
            .get()

          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0]
            this.acceptedVersion = userDoc.data().termsAcceptedVersion || 0

            const versionToLoad =
              this.acceptedVersion > 0
                ? this.acceptedVersion
                : this.latestVersion

            await this.loadTermsContent(versionToLoad)

            this.showAgreement = this.acceptedVersion < this.latestVersion
          } else {
            throw new Error('User record not found.')
          }
        } catch (err) {
          console.error('Error checking user acceptance:', err)
          this.error = 'Failed to verify acceptance status.'
        }
      },

      async acceptTerms() {
        if (!this.user || !this.user.uid) {
          this.error = 'User data is not available. Please log in again.'
          return
        }

        try {
          const querySnapshot = await db
            .collection('users')
            .where('uid', '==', this.user.uid)
            .get()

          if (!querySnapshot.empty) {
            const userDocId = querySnapshot.docs[0].id
            await db.collection('users').doc(userDocId).update({
              termsAcceptedVersion: this.latestVersion,
            })

            this.acceptedVersion = this.latestVersion
            this.showAgreement = false
          } else {
            throw new Error('User record not found. Cannot save acceptance.')
          }
        } catch (err) {
          console.error('Error saving acceptance:', err)
          this.error = 'Failed to save acceptance. Please try again.'
        }
      },
    },
    async created() {
      await this.checkUserAcceptance()
    },
  }
</script>

<style scoped>
  .headline {
    font-size: 24px;
    font-weight: bold;
  }
</style>
